import { FormEvent, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import styles from './LoginPage.module.css'

interface FormFields {
  email: string
  password: string
}

interface FormErrors {
  email?: string
  password?: string
}

const validate = (fields: FormFields): FormErrors => {
  const errors: FormErrors = {}
  if (!fields.email.trim()) {
    errors.email = "L'adresse email est requise."
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
    errors.email = "L'adresse email n'est pas valide."
  }

  if (!fields.password.trim()) {
    errors.password = 'Le mot de passe est requis.'
  }
  return errors
}

export function LoginPage() {
  const { login, loading } = useAuth()
  const navigate = useNavigate()

  const [fields, setFields] = useState<FormFields>({ email: '', password: '' })
  const [errors, setErrors] = useState<FormErrors>({})
  const [apiError, setApiError] = useState<string | null>(null)

  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target
    setFields((prev) => ({ ...prev, [name]: value }))
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
    if (apiError) setApiError(null)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const validation = validate(fields)
    if (Object.keys(validation).length > 0) {
      setErrors(validation)
      if (validation.email) emailRef.current?.focus()
      else if (validation.password) passwordRef.current?.focus()
      return
    }

    try {
      await login(fields.email, fields.password)
      navigate('/account')
    } catch (err: any) {
      setApiError(err.message)
    }
  }

  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <h1>Connexion</h1>
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <label htmlFor="email" className={styles.label}>
              Email
            </label>
            <input
              ref={emailRef}
              id="email"
              name="email"
              type="email"
              className={styles.input}
              value={fields.email}
              onChange={handleChange}
              disabled={loading}
            />
            {errors.email && (
              <div className={styles.errorMsg}>{errors.email}</div>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="password" className={styles.label}>
              Mot de passe
            </label>
            <input
              ref={passwordRef}
              id="password"
              name="password"
              type="password"
              className={styles.input}
              value={fields.password}
              onChange={handleChange}
              disabled={loading}
            />
            {errors.password && (
              <div className={styles.errorMsg}>{errors.password}</div>
            )}
          </div>

          {apiError && <div className={styles.apiError}>{apiError}</div>}

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={loading}
          >
            {loading ? '...' : 'Se connecter'}
          </button>
        </form>
      </div>
    </main>
  )
}
